import api from "./api";
import { getSuppliers } from "./supplierService";
import type {
  CreatePurchaseData,
  Purchase,
  PurchaseSummary,
  PurchasesSummary,
  SupplierListResponse,
  UpdatePurchaseData,
} from "../types/models";

export { getSuppliers };

/** Paginated list response (Story 5.3/5.4). */
export interface PurchaseListResponse {
  items: Purchase[];
  total: number;
  total_amount: number;
  page: number;
  limit: number;
  pages: number;
}

export type PurchaseSortBy =
  | "purchase_date"
  | "total"
  | "item_description"
  | "category"
  | "created_at"
  | "notes";
export type PurchaseSortOrder = "asc" | "desc";

export interface ListPurchasesParams {
  page?: number;
  limit?: number;
  from_date?: string | null;
  to_date?: string | null;
  supplier_id?: string | null;
  category?: string[] | string | null;
  notes?: string | null;
  item?: string | null;
  sort_by?: PurchaseSortBy | null;
  order?: PurchaseSortOrder | null;
}

/**
 * List purchases with optional filters (Story 5.4).
 */
export async function listPurchases(params?: ListPurchasesParams): Promise<PurchaseListResponse> {
  const page = params?.page ?? 1;
  const limit = params?.limit ?? 20;
  const search = new URLSearchParams();
  search.set("page", String(page));
  search.set("limit", String(limit));
  if (params?.from_date) search.set("from_date", params.from_date);
  if (params?.to_date) search.set("to_date", params.to_date);
  if (params?.supplier_id) search.set("supplier_id", params.supplier_id);
  if (params?.category) {
    const cat = Array.isArray(params.category) ? params.category.join(",") : params.category;
    if (cat) search.set("category", cat);
  }
  if (params?.notes) search.set("notes", params.notes);
  if (params?.item) search.set("item", params.item);
  if (params?.sort_by) search.set("sort_by", params.sort_by);
  if (params?.order) search.set("order", params.order);
  const response = await api.get<PurchaseListResponse>(`/purchases?${search.toString()}`);
  return response.data;
}

export interface GetPurchaseSummaryParams {
  from_date?: string | null;
  to_date?: string | null;
}

/**
 * Get purchases dashboard summary (last 7 days, current month, last month). Story 6-5-5.
 * For dashboard Purchases card. Admin and manager only.
 */
export async function getPurchasesDashboardSummary(): Promise<PurchasesSummary> {
  const response = await api.get<{
    last_7_days: string | number;
    current_month: string | number;
    last_month: string | number;
    today?: string | number;
    last_7_days_count?: number;
    current_month_count?: number;
    last_month_count?: number;
    today_count?: number;
    pending_funding_count?: number;
  }>("/purchases/dashboard-summary");
  const d = response.data;
  const toNum = (v: string | number | null | undefined): number => {
    if (v == null) return 0;
    const n = typeof v === "string" ? parseFloat(v) : v;
    return Number.isFinite(n) ? n : 0;
  };
  const toInt = (v: number | null | undefined): number =>
    typeof v === "number" && Number.isInteger(v) ? v : 0;
  return {
    last_7_days: toNum(d.last_7_days),
    current_month: toNum(d.current_month),
    last_month: toNum(d.last_month),
    today: toNum(d.today),
    last_7_days_count: toInt(d.last_7_days_count),
    current_month_count: toInt(d.current_month_count),
    last_month_count: toInt(d.last_month_count),
    today_count: toInt(d.today_count),
    pending_funding_count: toInt(d.pending_funding_count),
  };
}

/**
 * Get purchase summary (period totals, by_category, total_spent, top_suppliers). Story 5.4, 5.6.
 * Optional from_date, to_date scope by_category, total_spent, and top_suppliers to that range.
 */
export async function getPurchaseSummary(
  params?: GetPurchaseSummaryParams
): Promise<PurchaseSummary> {
  const search = new URLSearchParams();
  if (params?.from_date) search.set("from_date", params.from_date);
  if (params?.to_date) search.set("to_date", params.to_date);
  const qs = search.toString();
  const url = qs ? `/purchases/summary?${qs}` : "/purchases/summary";
  const response = await api.get<PurchaseSummary>(url);
  return response.data;
}

/**
 * Create a new purchase. Story 5.3
 * Backend computes subtotal, discount_amount, total.
 */
export async function createPurchase(data: CreatePurchaseData): Promise<Purchase> {
  const response = await api.post<Purchase>("/purchases", data);
  return response.data;
}

/**
 * Get a single purchase by id. Story 5.5
 */
export async function getPurchase(id: string): Promise<Purchase> {
  const response = await api.get<Purchase>(`/purchases/${id}`);
  return response.data;
}

/**
 * Update a purchase. Story 5.5, 10.5-3: catalog_item_id not in payload (read-only on edit).
 */
export async function updatePurchase(id: string, data: UpdatePurchaseData): Promise<Purchase> {
  const response = await api.put<Purchase>(`/purchases/${id}`, data);
  return response.data;
}

/**
 * Soft-delete a purchase. Story 5.5
 * Returns 204 No Content.
 */
export async function deletePurchase(id: string): Promise<void> {
  await api.delete(`/purchases/${id}`);
}

/**
 * List suppliers for purchase form supplier selector.
 * Re-export from supplierService for convenience.
 */
export type { SupplierListResponse };
